import { MoreHorizontal } from 'lucide-react';
import { getErrorMessage } from '@common/utils';
import { WarningMessage } from '@common/components';
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '../ui/button';
import {
  useMutateCopyProject,
  useMutateDeleteTellet,
  useMutateMoveProject,
} from '@/reactQuery/post';
import useTranslation from '@/hooks/useTranslation';
import Routes from '@/router/routes';
import { AlertDialogTrigger } from '../ui/alert-dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import RenameTelletModal from '../Modals/RenameTelletModal';
import { EProjectStatus, EStatusCode } from '@/enums';
import { ActionProps, CopyProjectFormData, MoveProjectFormData } from '@/types';

function WorkspaceProjectActionsDD({
  mergedWorkspaces,
  targettedProjectID,
  isUserOwner,
  isUserEditor,
  projectTitle,
  projectStatus,
}: ActionProps) {
  const { organization_id, workspace_id } = useParams();
  const { mutateAsync } = useMutateMoveProject(organization_id, workspace_id);
  const { mutateAsync: mutateAsyncCopyProject } = useMutateCopyProject(
    organization_id,
    workspace_id,
  );
  const { mutateAsync: mutateAsyncDeleteTellet, isPending: isDeletingProjectPending } =
    useMutateDeleteTellet(organization_id, workspace_id);
  const [openDeletingModal, setOpenDeletingModal] = useState(false);
  const { lang } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleDeleteTellet = async (project_id: string) => {
    try {
      await mutateAsyncDeleteTellet({ project_id });
      toast.success(lang.get('msg.projectDeleted'));
    } catch (err) {
      const axiosError = err as AxiosError;
      const errorMessage = getErrorMessage(axiosError);
      toast.error(errorMessage);
    }
  };

  const moveProject = async (formData: MoveProjectFormData) => {
    try {
      await mutateAsync(formData);
      toast.success(lang.get('msg.projectSuccessfullyMoved'));
    } catch (err) {
      const axiosError = err as AxiosError;
      if (axiosError?.status === EStatusCode.INVALID_ROLE) {
        toast.error(lang.get('msg.projectCanOnlyBeMovedByOwnerOfWorkspace'));
      } else {
        toast.error(lang.get('msg.errorPleaseTryAgain'));
      }
    }
  };

  const copyProject = async (formData: CopyProjectFormData) => {
    try {
      await mutateAsyncCopyProject(formData);
      toast.success(lang.get('msg.projectSuccessfullyCopied'));
    } catch (err) {
      const axiosError = err as AxiosError;
      if (axiosError?.status === EStatusCode.INVALID_ROLE) {
        toast.error(lang.get('msg.projectCanOnlyBeCopiedByOwnerOfWorkspace'));
      } else {
        toast.error(lang.get('msg.errorPleaseTryAgain'));
      }
    }
  };

  const isUserEditorOrOwner = isUserOwner || isUserEditor;
  const isProjectPublishedOrCompleted =
    projectStatus === EProjectStatus.PUBLISHED || projectStatus === EProjectStatus.COMPLETED;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="w-8 h-8 p-0">
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="mr-16 w-52 z-100">
        <DropdownMenuGroup>
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() =>
              navigate(`${pathname}${Routes.project_design.path}/${targettedProjectID}`)
            }
          >
            {lang.get('msg.view')}
          </DropdownMenuItem>
          {/* rename */}
          {isUserEditorOrOwner && (
            <RenameTelletModal project_id={targettedProjectID} projectTitle={projectTitle} />
          )}
          {/* copy to */}
          {isUserEditorOrOwner && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>{lang.get('msg.copyTo')}</DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  {mergedWorkspaces?.map((item) => (
                    <DropdownMenuItem
                      key={item?._id}
                      onClick={() =>
                        copyProject({
                          projectID: targettedProjectID,
                          targetWorkspaceId: item?._id,
                        })
                      }
                      className={`flex items-start py-2 pl-2 cursor-pointer pr-7
                       ${item?._id === workspace_id && 'bg-neutral-100'}`}
                    >
                      {item?.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
              {/* move to */}
              {isUserOwner && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>{lang.get('msg.moveTo')}</DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent>
                      {mergedWorkspaces?.map((item) => (
                        <DropdownMenuItem
                          key={item?._id}
                          onClick={() =>
                            !(item?._id === workspace_id) &&
                            moveProject({
                              projectID: targettedProjectID,
                              newWorkspaceId: item?._id,
                              oldWorkspaceId: workspace_id,
                            })
                          }
                          className={`flex items-start py-2 pl-2 cursor-pointer pr-7
                       ${item?._id === workspace_id && 'bg-neutral-100'}`}
                        >
                          {item?.name}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
              )}
            </DropdownMenuSub>
          )}
        </DropdownMenuGroup>
        {isUserEditorOrOwner && (
          <AlertDialog open={openDeletingModal}>
            <AlertDialogTrigger
              className="flex w-full px-2 py-1 mt-1 text-sm text-red-600 border-t rounded-sm hover:bg-gray-100"
              onClick={() => setOpenDeletingModal(true)}
            >
              {lang.get('msg.delete')}
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{lang.get('msg.areYouSure')}</AlertDialogTitle>
                <AlertDialogDescription className="text-dark-text">
                  {lang.get('msg.projectDeleteWarningPrefix')}
                  <span className="font-bold">{projectTitle}</span>
                  {lang.get('msg.projectDeleteWarningSuffix')}
                </AlertDialogDescription>
              </AlertDialogHeader>
              {isProjectPublishedOrCompleted && (
                <WarningMessage>
                  {lang.get('msg.completedOrPublishedProjectDeleteWarning')}
                </WarningMessage>
              )}
              <AlertDialogFooter className="mt-5">
                <AlertDialogCancel onClick={() => setOpenDeletingModal(false)}>
                  {lang.get('msg.cancel')}
                </AlertDialogCancel>
                <AlertDialogAction
                  disabled={isDeletingProjectPending}
                  onClick={() => {
                    setOpenDeletingModal(false);
                    handleDeleteTellet(targettedProjectID);
                  }}
                  className="bg-red-600 hover:bg-red-900"
                >
                  {lang.get('msg.delete')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default WorkspaceProjectActionsDD;
